import React from 'react';
import { useTheme } from '@/context/ThemeContext';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import {
  HERO_DATA, PRODUCTS, BENEFITS, SUCCESS_METRICS,
  IMPLEMENTATION_STEPS, TYPEFORM_URL
} from '@/data/mock';
import {
  SectionHeading, StatCard, CTAButton, VideoEmbed
} from '@/components/shared/SharedComponents';
import SEOHead, { organizationSchema, softwareSchema } from '@/components/shared/SEOHead';

const getIcon = (name) => LucideIcons[name];

export default function HomePage() {
  const { currentTheme: t, theme } = useTheme();

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      <SEOHead
        title="Motivy - Plataforma de Capital Humano y Bienestar Corporativo | Software de RRHH"
        description="Motivy es la plataforma líder de capital humano y bienestar corporativo. Employee engagement, reconocimiento laboral, FitRewards, salud mental y fidelización de clientes. +300K usuarios activos en +50 empresas."
        keywords="plataforma capital humano, software recursos humanos, bienestar corporativo, reconocimiento laboral, engagement empleados, FitRewards, salud mental corporativa, fidelización clientes, incentivos laborales, cultura organizacional, MotiCare, comunicación interna, gamificación empresarial"
        structuredData={[organizationSchema, softwareSchema]}
      />
      {/* Hero */}
      <section className="relative pt-28 lg:pt-36 pb-16 lg:pb-24 overflow-hidden" style={{ background: t.heroBg }}>
        {/* Aurora blobs */}
        {theme === 'aurora' && (
          <>
            <div className="absolute top-20 left-10 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{ background: 'radial-gradient(circle, rgba(58,113,181,0.4), transparent)' }} />
            <div className="absolute bottom-10 right-10 w-80 h-80 rounded-full opacity-15 blur-3xl" style={{ background: 'radial-gradient(circle, rgba(240,88,100,0.3), transparent)' }} />
          </>
        )}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium mb-6"
                style={{ background: t.badgeBg, color: t.badgeText }}
              >
                +300K usuarios activos
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight mb-6"
                style={{ color: t.text, fontFamily: 'Manrope, sans-serif' }}
              >
                {HERO_DATA.heading}
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-base lg:text-lg leading-relaxed mb-8 max-w-lg"
                style={{ color: t.textSecondary }}
              >
                {HERO_DATA.subtitle}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex flex-wrap gap-4"
              >
                <CTAButton href={TYPEFORM_URL} />
                <CTAButton
                  text="Ver soluciones"
                  variant="secondary"
                  onClick={() => document.getElementById('productos')?.scrollIntoView({ behavior: 'smooth' })}
                />
              </motion.div>
            </div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="relative"
            >
              <img
                src={HERO_DATA.image}
                alt="Plataforma Motivy"
                className="w-full rounded-2xl"
                style={{ boxShadow: `0 30px 60px ${t.shadowColor}` }}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Products */}
      <section id="productos" className="py-16 lg:py-24" style={{ background: t.bg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            heading="Explora nuestra plataforma de capital humano"
            subtitle="Programas de bienestar corporativo hechos a tu medida para transformar la cultura de tu empresa."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PRODUCTS.map((product, i) => {
              const Icon = getIcon(product.icon);
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                >
                  <Link
                    to={product.link}
                    className="block h-full p-6 rounded-xl border transition-all duration-300 group"
                    style={{
                      background: t.cardBg,
                      borderColor: t.cardBorder,
                      backdropFilter: theme === 'aurora' ? 'blur(10px)' : 'none',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = `0 12px 32px ${t.shadowHover}`;
                      e.currentTarget.style.transform = 'translateY(-4px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: t.featureIconBg }}>
                      {Icon && <Icon size={22} style={{ color: t.featureIconColor }} />}
                    </div>
                    <h3 className="text-lg font-semibold mb-2" style={{ color: t.text, fontFamily: 'Manrope, sans-serif' }}>{product.title}</h3>
                    <p className="text-sm leading-relaxed mb-4" style={{ color: t.textSecondary }}>{product.description}</p>
                    <span className="inline-flex items-center gap-1 text-sm font-medium group-hover:gap-2 transition-all duration-200" style={{ color: t.accent }}>
                      Leer más <ArrowRight size={14} />
                    </span>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Video 1 */}
      <section className="py-16 lg:py-24" style={{ background: t.sectionAltBg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <SectionHeading
                heading="Transforma la cultura de tu empresa con una solución integral"
                subtitle="A través de programas de bienestar corporativo mejoramos la experiencia de tu activo más importante: la gente."
                centered={false}
              />
            </div>
            <VideoEmbed url="https://www.youtube.com/embed/eY_73uImpeI" title="Motivy, el valor del reconocimiento" />
          </div>
        </div>
      </section>

      {/* Video 2 */}
      <section className="py-16 lg:py-24" style={{ background: t.bg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <VideoEmbed url="https://www.youtube.com/embed/Ou7t4f2kUlI" title="FitRewards by Motivy" />
            </div>
            <div className="order-1 lg:order-2">
              <SectionHeading
                heading="Reduce el ausentismo y la rotación con una cultura de bienestar real"
                subtitle="FitRewards promueve hábitos saludables entre colaboradores para construir equipos más comprometidos y estables."
                centered={false}
              />
              <CTAButton href={TYPEFORM_URL} />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 lg:py-24" style={{ background: t.sectionAltBg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((b, i) => {
              const Icon = getIcon(b.icon);
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="flex items-start gap-4 p-5 rounded-xl"
                >
                  <div className="w-10 h-10 shrink-0 rounded-lg flex items-center justify-center" style={{ background: t.featureIconBg }}>
                    {Icon && <Icon size={18} style={{ color: t.featureIconColor }} />}
                  </div>
                  <p className="text-sm leading-relaxed" style={{ color: t.textSecondary }}>{b.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Success Metrics */}
      <section className="py-16 lg:py-24" style={{ background: t.bg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            heading="Casos de éxito con nuestra plataforma"
            subtitle="Descubre cómo nuestro software de recursos humanos ha sido clave para el éxito empresarial de nuestros clientes."
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-6">
            {SUCCESS_METRICS.map((m, i) => <StatCard key={i} {...m} index={i} />)}
          </div>
        </div>
      </section>

      {/* Implementation */}
      <section className="py-16 lg:py-24" style={{ background: t.sectionAltBg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading heading="Implementación de la plataforma" />
          <div className="grid md:grid-cols-3 gap-8">
            {IMPLEMENTATION_STEPS.map((step, i) => {
              const Icon = getIcon(step.icon);
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 }}
                  className="text-center p-8 rounded-xl border"
                  style={{ background: t.cardBg, borderColor: t.cardBorder }}
                >
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ background: t.featureIconBg }}>
                    {Icon && <Icon size={24} style={{ color: t.featureIconColor }} />}
                  </div>
                  <h3 className="text-lg font-semibold mb-3" style={{ color: t.text, fontFamily: 'Manrope, sans-serif' }}>{step.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: t.textSecondary }}>{step.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
